import { ENTITY, KINDS } from "@/lib/kinds";
import { mediaUrl } from "@/lib/api";
import { chipStyle, colorFor, initials } from "@/lib/format";
import { cn } from "@/lib/utils";

export function EntityBadge({ type, className }) {
  const entity = ENTITY[type];
  const Icon = entity.icon;
  return (
    <span className={cn("inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide", entity.cls, className)}>
      <Icon className="h-3 w-3" /> {entity.label}
    </span>
  );
}

export function GroupIcon({ group, className = "h-4 w-4", box = false }) {
  const Icon = KINDS[group.kind]?.icon;
  if (group.image) return <img src={mediaUrl(group.image)} alt="" className={cn("rounded object-cover", className)} />;
  if (group.emoji) return <span className="leading-none">{group.emoji}</span>;
  if (!box) return <Icon className={className} />;
  return (
    <span className="flex h-full w-full items-center justify-center rounded-[inherit]" style={{ backgroundColor: `${group.color || "#A4247F"}18`, color: group.color || "#A4247F" }}>
      <Icon className={className} />
    </span>
  );
}

export function GroupChip({ group, size = "sm", className }) {
  return (
    <span
      data-testid={`group-chip-${group.id}`}
      style={chipStyle(group.color || "#78716C")}
      className={cn(
        "inline-flex max-w-full items-center gap-1 rounded-full border font-semibold",
        size === "sm" ? "px-2 py-0.5 text-[11px]" : "px-3 py-1 text-xs",
        className,
      )}
    >
      <GroupIcon group={group} className={size === "sm" ? "h-3 w-3" : "h-3.5 w-3.5"} />
      <span className="truncate">{group.name}</span>
    </span>
  );
}

export function GroupChips({ person, max = 3 }) {
  const all = [...(person.clubs || []), ...(person.commissions || []), ...(person.groups || [])];
  if (!all.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      {all.slice(0, max).map((g) => <GroupChip key={g.id} group={g} />)}
      {all.length > max && (
        <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[11px] font-semibold text-stone-600">+{all.length - max}</span>
      )}
    </div>
  );
}

const SIZES = { sm: "h-9 w-9 text-xs", md: "h-12 w-12 text-sm", lg: "h-20 w-20 text-xl", xl: "h-24 w-24 text-2xl" };

export function PersonAvatar({ person, size = "md", className }) {
  const name = person.full_name || "";
  if (person.photo) {
    return <img src={mediaUrl(person.photo)} alt={name} className={cn("shrink-0 rounded-2xl object-cover", SIZES[size], className)} />;
  }
  const color = colorFor(name);
  return (
    <span className={cn("flex shrink-0 items-center justify-center rounded-2xl font-heading font-bold", SIZES[size], className)}
      style={{ backgroundColor: `${color}1A`, color }}>
      {initials(name)}
    </span>
  );
}

export function OrgLogo({ org, size = "md", className }) {
  const name = org?.name || "";
  if (org?.logo) {
    return (
      <span className={cn("flex shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-border bg-white p-1.5", SIZES[size], className)}>
        <img src={mediaUrl(org.logo)} alt={name} className="max-h-full max-w-full object-contain" />
      </span>
    );
  }
  const color = colorFor(name);
  return (
    <span className={cn("flex shrink-0 items-center justify-center rounded-2xl border font-heading font-extrabold", SIZES[size], className)}
      style={{ backgroundColor: `${color}10`, color, borderColor: `${color}30` }}>
      {initials(name)}
    </span>
  );
}
